import React, { useState, Fragment } from 'react';
import { Table, Checkbox, Text, Badge, Group, ActionIcon, Tooltip, ScrollArea, Button, Box, Menu, ThemeIcon, Avatar } from '@mantine/core';
import { ChevronDown, ChevronRight, ExternalLink, ChevronsDown, ChevronsUp, MoreHorizontal, FileText, User, Building2, Calendar, AlertCircle } from 'lucide-react';
import { Loan, PipelineSection } from '../types/loan';
interface UnifiedLoanGridProps {
  loans: Record<string, Loan[]>;
  selectedLoanIds: string[];
  onSelectionChange: (loanIds: string[]) => void;
  onLoanClick: (loan: Loan) => void;
  groupByStage: boolean;
}
export function UnifiedLoanGrid({
  loans,
  selectedLoanIds,
  onSelectionChange,
  onLoanClick,
  groupByStage
}: UnifiedLoanGridProps) {
  const allSections: PipelineSection[] = ['CTC', 'CD OUT', 'ALL IN', 'AWC', 'SUBMITTED'];
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(allSections));
  const allLoans = Object.entries(loans).flatMap(([section, sectionLoans]) => sectionLoans.map(loan => ({
    ...loan,
    section: section as PipelineSection
  })));
  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };
  const expandAll = () => {
    setExpandedSections(new Set(allSections));
  };
  const collapseAll = () => {
    setExpandedSections(new Set());
  };
  const toggleLoanSelection = (loanId: string) => {
    if (selectedLoanIds.includes(loanId)) {
      onSelectionChange(selectedLoanIds.filter(id => id !== loanId));
    } else {
      onSelectionChange([...selectedLoanIds, loanId]);
    }
  };
  const toggleSectionSelection = (section: string) => {
    const sectionLoanIds = (loans[section] || []).map(l => l.id);
    const allSelected = sectionLoanIds.every(id => selectedLoanIds.includes(id));
    if (allSelected) {
      onSelectionChange(selectedLoanIds.filter(id => !sectionLoanIds.includes(id)));
    } else {
      const newSelection = [...new Set([...selectedLoanIds, ...sectionLoanIds])];
      onSelectionChange(newSelection);
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const getLoanNumberColor = (loan: Loan & {
    section: PipelineSection;
  }) => {
    if (loan.loanNumberColor && loan.loanNumberColor !== 'default') {
      return loan.loanNumberColor;
    }
    const rules: Record<PipelineSection, number> = {
      SUBMITTED: 3,
      AWC: 12,
      'ALL IN': 5,
      'CD OUT': 5,
      CTC: 3
    };
    const threshold = rules[loan.section];
    if (loan.daysInStage >= threshold) {
      return 'red';
    }
    return 'default';
  };
  const getColorBadge = (color: string) => {
    const colorMap: Record<string, string> = {
      red: 'red',
      yellow: 'yellow',
      orange: 'orange',
      lightBlue: 'blue',
      default: 'gray'
    };
    return colorMap[color] || 'gray';
  };
  const renderLoanRow = (loan: Loan & {
    section: PipelineSection;
  }) => {
    const isSelected = selectedLoanIds.includes(loan.id);
    const loanNumberColor = getLoanNumberColor(loan);
    const incompleteConditions = [...loan.outstandingConditions, ...loan.loanOfficerConditions].filter(c => !c.completed);
    return <Table.Tr key={loan.id} style={{
      backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
    }} onClick={() => onLoanClick(loan)} className="group" data-id="element-466">
        <Table.Td style={{
        width: '40px',
        paddingLeft: '16px'
      }} onClick={e => e.stopPropagation()} data-id="element-467">
          <Checkbox checked={isSelected} onChange={() => toggleLoanSelection(loan.id)} color="blue" size="sm" radius="sm" data-id="element-468" />
        </Table.Td>

        <Table.Td data-id="element-469">
          <Group gap="xs" data-id="element-470">
            <ThemeIcon variant="light" color={getColorBadge(loanNumberColor)} size="md" radius="md" data-id="element-471">
              <FileText size={14} data-id="element-472" />
            </ThemeIcon>
            <Box data-id="element-473">
              <Text size="sm" fw={600} style={{
              color: 'rgba(255, 255, 255, 0.9)'
            }} lh={1.2} data-id="element-474">
                {loan.loanNumber}
              </Text>
              <Text size="xs" style={{
              color: 'rgba(255, 255, 255, 0.4)'
            }} lh={1.2} data-id="element-475">
                {loan.program}
              </Text>
            </Box>
          </Group>
        </Table.Td>

        <Table.Td data-id="element-476">
          <Group gap="xs" data-id="element-477">
            <Avatar size="sm" radius="xl" color="blue" name={loan.clientName} data-id="element-478">
              {loan.clientName.charAt(0)}
            </Avatar>
            <Box data-id="element-479">
              <Text size="sm" fw={600} style={{
              color: 'rgba(255, 255, 255, 0.9)'
            }} lh={1.2} data-id="element-480">
                {loan.clientName}
              </Text>
              <Text size="xs" style={{
              color: 'rgba(255, 255, 255, 0.4)'
            }} lh={1.2} data-id="element-481">
                {loan.state}
              </Text>
            </Box>
          </Group>
        </Table.Td>

        <Table.Td data-id="element-482">
          <Text size="sm" style={{
          color: 'rgba(255, 255, 255, 0.6)'
        }} data-id="element-483">
            {loan.loanOfficerAssistant}
          </Text>
        </Table.Td>

        <Table.Td data-id="element-484">
          <Group gap={6} data-id="element-485">
            <Building2 size={14} color="rgba(255, 255, 255, 0.4)" data-id="element-486" />
            <Text size="sm" style={{
            color: 'rgba(255, 255, 255, 0.6)'
          }} data-id="element-487">
              {loan.lender}
            </Text>
          </Group>
        </Table.Td>

        <Table.Td data-id="element-488">
          <Text size="sm" fw={600} ta="right" style={{
          color: 'rgba(255, 255, 255, 0.9)'
        }} data-id="element-489">
            {formatCurrency(loan.amount)}
          </Text>
        </Table.Td>

        <Table.Td data-id="element-490">
          <Group gap={6} data-id="element-491">
            <Calendar size={14} color="rgba(255, 255, 255, 0.4)" data-id="element-492" />
            <Text size="sm" style={{
            color: 'rgba(255, 255, 255, 0.6)'
          }} data-id="element-493">
              {loan.submittedDate}
            </Text>
          </Group>
        </Table.Td>

        <Table.Td data-id="element-494">
          <Badge variant="light" color={loan.daysInStage >= 5 ? 'red' : loan.daysInStage >= 3 ? 'orange' : 'green'} size="sm" radius="sm" data-id="element-495">
            {loan.daysInStage}d
          </Badge>
        </Table.Td>

        <Table.Td data-id="element-496">
          {incompleteConditions.length > 0 ? <Tooltip label={incompleteConditions.map(c => c.text).join(', ')} multiline w={300} withArrow data-id="element-497">
              <Badge variant="outline" color="orange" size="sm" radius="sm" leftSection={<AlertCircle size={12} data-id="element-499" />} data-id="element-498">
                {incompleteConditions.length} pending
              </Badge>
            </Tooltip> : <Badge variant="light" color="gray" size="sm" radius="sm" style={{
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          color: 'rgba(255, 255, 255, 0.4)'
        }} data-id="element-500">
              None
            </Badge>}
        </Table.Td>

        <Table.Td onClick={e => e.stopPropagation()} data-id="element-501">
          <Group gap={4} justify="flex-end" data-id="element-502">
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => onLoanClick(loan)} style={{
            opacity: 0,
            transition: 'opacity 0.2s'
          }} className="group-hover:opacity-100" data-id="element-503">
              <ExternalLink size={16} color="rgba(255, 255, 255, 0.5)" data-id="element-504" />
            </ActionIcon>
            <Menu position="bottom-end" shadow="md" data-id="element-505">
              <Menu.Target data-id="element-506">
                <ActionIcon variant="subtle" color="gray" size="sm" style={{
                opacity: 0,
                transition: 'opacity 0.2s'
              }} className="group-hover:opacity-100" data-id="element-507">
                  <MoreHorizontal size={16} color="rgba(255, 255, 255, 0.5)" data-id="element-508" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown style={{
              backgroundColor: '#1a2332',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }} data-id="element-509">
                <Menu.Item leftSection={<FileText size={14} data-id="element-511" />} style={{
                color: 'rgba(255, 255, 255, 0.8)'
              }} data-id="element-510">
                  View Details
                </Menu.Item>
                <Menu.Item leftSection={<User size={14} data-id="element-513" />} style={{
                color: 'rgba(255, 255, 255, 0.8)'
              }} data-id="element-512">
                  Contact Client
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Table.Td>
      </Table.Tr>;
  };
  const renderGroupedView = () => {
    return allSections.map(section => {
      const sectionLoans = (loans[section] || []).map(loan => ({
        ...loan,
        section
      }));
      const isExpanded = expandedSections.has(section);
      const sectionLoanIds = sectionLoans.map(l => l.id);
      const allSelected = sectionLoanIds.length > 0 && sectionLoanIds.every(id => selectedLoanIds.includes(id));
      const someSelected = sectionLoanIds.some(id => selectedLoanIds.includes(id)) && !allSelected;
      return <Fragment key={section} data-id="element-514">
          <Table.Tr style={{
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          cursor: 'pointer',
          borderBottom: isExpanded ? '1px solid rgba(255, 255, 255, 0.06)' : 'none',
          transition: 'background-color 0.15s'
        }} onClick={() => toggleSection(section)} data-id="element-515">
            <Table.Td colSpan={10} style={{
            padding: '12px 16px'
          }} data-id="element-516">
              <Group justify="space-between" data-id="element-517">
                <Group gap="md" data-id="element-518">
                  <ActionIcon variant="subtle" color="gray" size="sm" onClick={e => {
                  e.stopPropagation();
                  toggleSection(section);
                }} data-id="element-519">
                    {isExpanded ? <ChevronDown size={18} color="rgba(255, 255, 255, 0.5)" data-id="element-520" /> : <ChevronRight size={18} color="rgba(255, 255, 255, 0.5)" data-id="element-521" />}
                  </ActionIcon>

                  <Checkbox checked={allSelected} indeterminate={someSelected} onChange={() => toggleSectionSelection(section)} onClick={e => e.stopPropagation()} color="blue" size="sm" radius="sm" data-id="element-522" />

                  <Group gap="xs" data-id="element-523">
                    <Text fw={700} size="sm" style={{
                    color: 'rgba(255, 255, 255, 0.9)'
                  }} data-id="element-524">
                      {section}
                    </Text>
                    <Badge variant="light" color="blue" size="sm" radius="sm" data-id="element-525">
                      {sectionLoans.length}
                    </Badge>
                  </Group>
                </Group>

                <Text size="xs" style={{
                color: 'rgba(255, 255, 255, 0.4)'
              }} fw={500} data-id="element-526">
                  Total Volume:{' '}
                  <Text span fw={600} style={{
                  color: 'rgba(255, 255, 255, 0.7)'
                }} data-id="element-527">
                    {formatCurrency(sectionLoans.reduce((sum, l) => sum + l.amount, 0))}
                  </Text>
                </Text>
              </Group>
            </Table.Td>
          </Table.Tr>
          {isExpanded && sectionLoans.map(renderLoanRow)}
        </Fragment>;
    });
  };
  const renderFlatView = () => {
    return allLoans.map(renderLoanRow);
  };
  return <Box data-id="element-528">
      {/* Table Controls Header */}
      {groupByStage && <Group justify="flex-end" p="xs" style={{
      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
      backgroundColor: 'rgba(255, 255, 255, 0.04)'
    }} data-id="element-529">
          <Button.Group data-id="element-530">
            <Button variant="default" size="xs" leftSection={<ChevronsDown size={14} data-id="element-532" />} onClick={expandAll} styles={{
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }
        }} data-id="element-531">
              Expand All
            </Button>
            <Button variant="default" size="xs" leftSection={<ChevronsUp size={14} data-id="element-534" />} onClick={collapseAll} styles={{
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }
        }} data-id="element-533">
              Collapse All
            </Button>
          </Button.Group>
        </Group>}

      <ScrollArea data-id="element-535">
        <Table verticalSpacing="sm" highlightOnHover={false} withTableBorder={false} withColumnBorders={false} styles={{
        table: {
          backgroundColor: 'transparent'
        }
      }} data-id="element-536">
          <Table.Thead data-id="element-537">
            <Table.Tr style={{
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }} data-id="element-538">
              <Table.Th style={{
              width: 40,
              paddingLeft: 16
            }} data-id="element-539">
                <Checkbox checked={allLoans.length > 0 && allLoans.every(l => selectedLoanIds.includes(l.id))} indeterminate={allLoans.some(l => selectedLoanIds.includes(l.id)) && !allLoans.every(l => selectedLoanIds.includes(l.id))} onChange={() => {
                if (allLoans.every(l => selectedLoanIds.includes(l.id))) {
                  onSelectionChange([]);
                } else {
                  onSelectionChange(allLoans.map(l => l.id));
                }
              }} color="blue" size="sm" radius="sm" data-id="element-540" />
              </Table.Th>
              <Table.Th style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600
            }} data-id="element-541">
                Loan Details
              </Table.Th>
              <Table.Th style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600
            }} data-id="element-542">
                Client
              </Table.Th>
              <Table.Th style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600
            }} data-id="element-543">
                Assistant
              </Table.Th>
              <Table.Th style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600
            }} data-id="element-544">
                Lender
              </Table.Th>
              <Table.Th style={{
              textAlign: 'right',
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600
            }} data-id="element-545">
                Amount
              </Table.Th>
              <Table.Th style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600
            }} data-id="element-546">
                Submitted
              </Table.Th>
              <Table.Th style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600
            }} data-id="element-547">
                Age
              </Table.Th>
              <Table.Th style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600
            }} data-id="element-548">
                Conditions
              </Table.Th>
              <Table.Th style={{
              width: 60
            }} data-id="element-549"></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody data-id="element-550">
            {groupByStage ? renderGroupedView() : renderFlatView()}
            {allLoans.length === 0 && <Table.Tr data-id="element-551">
                <Table.Td colSpan={10} data-id="element-552">
                  <Text ta="center" py="xl" style={{
                color: 'rgba(255, 255, 255, 0.4)'
              }} data-id="element-553">
                    No loans match the current filters
                  </Text>
                </Table.Td>
              </Table.Tr>}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Box>;
}